/**
 * Vague 20 — Switcher de vue actif
 *
 * Dropdown en haut de la sidebar / header qui permet de :
 *  - Voir la vue active
 *  - Changer de vue (presets système / org / perso)
 *  - Accéder à la gestion des vues
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Settings, Check, Sparkles, Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useActiveView } from "@/lib/useActiveView";

export default function ViewSwitcher() {
  const navigate = useNavigate();
  const { view, setActiveView } = useActiveView();
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/workspace/views")
      .then((r) => setViews(r.data?.items || []))
      .catch((e) => notify.error(fmtError(e, "Vues indisponibles")))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const select = async (v) => {
    setOpen(false);
    if (v.id === view?.id) return;
    try {
      await setActiveView(v.id);
      notify.success(`Vue active : ${v.name}`);
    } catch (e) {
      notify.error(fmtError(e, "Activation échouée"));
    }
  };

  const grouped = {
    system: views.filter((v) => v.scope === "system"),
    org: views.filter((v) => v.scope === "org"),
    personal: views.filter((v) => v.scope === "personal"),
  };

  return (
    <div ref={rootRef} className="relative" data-testid="view-switcher">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded border border-white/10 bg-white/[0.02] hover:border-[#7F77DD]/40 text-xs text-zinc-300 transition"
        data-testid="view-switcher-toggle"
      >
        <Sparkles className="w-3 h-3 text-[#7F77DD] flex-shrink-0" />
        <span className="flex-1 text-left truncate">{view?.name || "Standard"}</span>
        <ChevronDown className="w-3 h-3 text-zinc-500 flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-[55] bg-[#0a0a0a] border border-white/10 rounded shadow-2xl max-h-96 overflow-y-auto" data-testid="view-switcher-menu">
          {loading && (
            <div className="px-3 py-3 text-center text-zinc-500 text-xs">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…
            </div>
          )}
          {!loading && (
            <>
              {Object.entries(grouped).map(([scope, list]) => {
                if (list.length === 0) return null;
                const scopeLabel = scope === "system" ? "Presets système" : scope === "org" ? "Vues d'organisation" : "Mes vues";
                return (
                  <div key={scope}>
                    <div className="px-3 pt-2 pb-1 text-[9px] uppercase tracking-wider text-zinc-500">{scopeLabel}</div>
                    {list.map((v) => {
                      const isActive = v.id === view?.id;
                      return (
                        <button
                          key={v.id}
                          onClick={() => select(v)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/[0.04] transition ${isActive ? "bg-[#7F77DD]/10 text-[#7F77DD]" : "text-zinc-300"}`}
                          data-testid={`view-switcher-item-${v.id}`}
                        >
                          {isActive ? <Check className="w-3 h-3" /> : <span className="w-3" />}
                          <span className="flex-1 text-left truncate">{v.name}</span>
                          <span className="text-[10px] text-zinc-500">{(v.modules || []).filter((m) => m.visible !== false).length}</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              <div className="border-t border-white/10 mt-1">
                <button
                  onClick={() => { setOpen(false); navigate("/app/settings/views"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:bg-white/[0.04] transition"
                  data-testid="view-switcher-manage"
                >
                  <Settings className="w-3 h-3" />
                  Gérer mes vues
                </button>
                <button
                  onClick={() => { setOpen(false); navigate("/app/settings/views?new=1"); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#7F77DD] hover:bg-white/[0.04] transition"
                  data-testid="view-switcher-new"
                >
                  <Plus className="w-3 h-3" />
                  Nouvelle vue
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
